import { requestErrorDetails } from '../shared/request-error.js';
import type { MapAnalyticsTrack } from '../shared/analytics-schema.js';
import type { SchemaInput } from '../shared/schema-input.js';

export type FeatureFailure = 'network' | 'http' | 'invalid_input' | 'not_allowed' | 'clipboard' | 'unknown';
export type GlobeEndReason = 'toggle' | 'action_requires_map' | 'render_error' | 'page_hidden';

export function opacityPercent(value: number): string {
  const safeValue = Number.isFinite(value) ? value : 1;
  return String(Math.round(Math.max(0, Math.min(1, safeValue)) * 100));
}

export function routeStepCountBucket(count: number): '1_5' | '6_10' | '11_20' | '21_plus' {
  if (count <= 5) return '1_5';
  if (count <= 10) return '6_10';
  if (count <= 20) return '11_20';
  return '21_plus';
}

export function routeAlternativeBucket(index: number): 'first' | 'second' | 'later' {
  if (index <= 0) return 'first';
  if (index === 1) return 'second';
  return 'later';
}

export function featureFailure(error: SchemaInput): FeatureFailure {
  if (error instanceof TypeError) return 'network';
  const message = error instanceof Error ? error.message.toLowerCase() : '';
  if (message.includes('clipboard') || message.includes('copy')) return 'clipboard';
  if (message.includes('forbidden') || message.includes('not_allowed') || message.includes('pro_required')) return 'not_allowed';
  if (requestErrorDetails(error).http_status !== undefined) return 'http';
  if (message.includes('invalid') || message.includes('required')) return 'invalid_input';
  if (message.includes('http') || message.includes('failed')) return 'http';
  return 'unknown';
}

function globeDwellBucket(milliseconds: number): 'lt_10s' | '10_59s' | '1_4m' | '5m_plus' {
  if (milliseconds < 10_000) return 'lt_10s';
  if (milliseconds < 60_000) return '10_59s';
  if (milliseconds < 300_000) return '1_4m';
  return '5m_plus';
}

export function createGlobeMeasurement(track: MapAnalyticsTrack, now: () => number = () => performance.now()) {
  let startedAt: number | null = null;
  return {
    start(): void {
      startedAt = now();
    },
    end(reason: GlobeEndReason): void {
      if (startedAt === null) return;
      const dwell = globeDwellBucket(Math.max(0, now() - startedAt));
      startedAt = null;
      track('globe_session_ended', { reason, dwell });
    },
  };
}
